import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  UserProfile,
  UserRole,
  UserContextType,
  UserProfileUpdate,
  hasPermission as checkPermission,
  isSystemAdmin as checkSystemAdmin,
  isManagerOrAbove as checkManagerOrAbove
} from '../types/user';
import { httpClient } from '../services/httpClient';
import { useUserRole } from '../hooks/useUserRole';
import logger from '../utils/logger';

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user: authUser, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 🆕 Phase 1-4: Get Cognito groups as primary role source
  const { roleInfo: cognitoRoleInfo, loading: cognitoLoading } = useUserRole();

  // Primary role determination from Cognito User Pool Groups
  const getPrimaryUserRole = useCallback((): UserRole | null => {
    const cognitoRole = cognitoRoleInfo?.primaryRole;
    
    if (cognitoRole) {
      if (cognitoRole === 'SYSTEM_ADMIN') return UserRole.SYSTEM_ADMIN;
      if (cognitoRole === 'MANAGER') return UserRole.MANAGER;
      if (cognitoRole === 'USER') return UserRole.USER;
    }
    
    return null;
  }, [cognitoRoleInfo]);

  const userRole = getPrimaryUserRole();

  // Permission check functions
  const hasPermission = useCallback((requiredRole: UserRole): boolean => {
    return checkPermission(userRole, requiredRole);
  }, [userRole]);

  const isSystemAdmin = useCallback((): boolean => {
    return checkSystemAdmin(userRole);
  }, [userRole]);

  const isManagerOrAbove = useCallback((): boolean => {
    return checkManagerOrAbove(userRole);
  }, [userRole]);

  // Fetch user profile from API
  const fetchProfile = useCallback(async () => {
    if (!authUser) {
      setProfile(null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const userData = await httpClient.get<UserProfile>('/users/me');
      
      setProfile(userData);

    } catch (err: any) {
      logger.error('❌ [UserContext] Error fetching user profile:', err);

      // Handle different error scenarios
      if (err.response?.status === 401) {
        setError('認証が必要です');
        setProfile(null);
      } else if (err.response?.status === 404) {
        setError(null);
        setProfile(null);
      } else {
        setError('ユーザープロファイルの取得に失敗しました');
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  // Update user profile
  const updateProfile = useCallback(async (data: UserProfileUpdate): Promise<void> => {
    if (!authUser) {
      throw new Error('ログインが必要です');
    }

    try {
      setLoading(true);
      setError(null);
      
      const updatedProfile = await httpClient.put<UserProfile>('/users/me', data);
      
      setProfile(updatedProfile);

    } catch (err: any) {
      logger.error('❌ [UserContext] Error updating user profile:', err);

      if (err.response?.status === 401) {
        setError('認証が必要です');
      } else if (err.response?.status === 404) {
        setError('ユーザープロファイルが見つかりません');
      } else {
        setError('ユーザープロファイルの更新に失敗しました');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  // Refresh profile (alias for fetchProfile)
  const refreshProfile = useCallback(async (): Promise<void> => {
    await fetchProfile();
  }, [fetchProfile]);

  // Effect to fetch profile when auth user changes
  useEffect(() => {
    // Don't fetch profile while auth is still loading
    if (authLoading) {
      return;
    }

    fetchProfile();
  }, [authUser, authLoading, fetchProfile]);

  // Clear profile when auth user is cleared
  useEffect(() => {
    if (!authUser) {
      setProfile(null);
      setError(null);
    }
  }, [authUser]);

  const contextValue: UserContextType = {
    profile,
    userRole,
    loading: loading || authLoading || (!!authUser && cognitoLoading),
    error,
    updateProfile,
    refreshProfile,
    hasPermission,
    isSystemAdmin,
    isManagerOrAbove,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};